import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/ui/Sidebar';
import Header from '../../components/ui/Header';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import PageTreeNode from './components/PageTreeNode';
import BulkActionsBar from './components/BulkActionsBar';
import PageFilters from './components/PageFilters';
import PageStatsCard from './components/PageStatsCard';
import ViewToggle from './components/ViewToggle';
import QuickActions from './components/QuickActions';
import { pagesService, auditService } from '../../lib/services';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';

const PageManager = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [currentView, setCurrentView] = useState('tree');
  const [selectedPages, setSelectedPages] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({ status: 'all', author: 'all', sort: 'modified' });
  const [pagesData, setPagesData] = useState([]);
  const [pageStats, setPageStats] = useState({ total: 0, published: 0, draft: 0, archived: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { user } = useAuth();

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/dashboard-home', icon: 'Home' },
    { label: 'Page Manager', path: '/page-manager' }
  ];

  const loadPages = async () => {
    setLoading(true);
    setError('');
    try {
      const [pagesResult, statsResult] = await Promise.all([
        pagesService?.getAll({ status: filters?.status, search: searchQuery }),
        pagesService?.getStats()
      ]);

      if (pagesResult?.error) throw pagesResult?.error;
      if (statsResult?.error) throw statsResult?.error;

      // Map DB data to tree node format
      const mapped = (pagesResult?.data || [])?.map(p => ({
        id: p?.id,
        title: p?.title || 'Untitled',
        url: p?.slug || '/',
        status: p?.status || 'draft',
        author: p?.author?.fullName || 'Unknown',
        lastModified: p?.updatedAt ? new Date(p.updatedAt)?.toLocaleDateString() : 'Unknown',
        views: '—',
        seoScore: 0,
        thumbnail: null,
        thumbnailAlt: `Thumbnail for ${p?.title || 'page'}`,
        children: []
      }));

      setPagesData(mapped);
      setPageStats(statsResult?.data);
    } catch (err) {
      setError(err?.message || 'Failed to load pages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPages();
  }, [filters?.status, searchQuery]);

  const stats = [
    { icon: 'FileText', label: 'Total Pages', value: String(pageStats?.total), change: `+${pageStats?.published}`, changeType: 'positive' },
    { icon: 'CheckCircle2', label: 'Published', value: String(pageStats?.published), change: 'Live', changeType: 'positive' },
    { icon: 'FileEdit', label: 'Drafts', value: String(pageStats?.draft), change: 'Pending', changeType: 'neutral' },
    { icon: 'Archive', label: 'Archived', value: String(pageStats?.archived), change: 'Stored', changeType: 'neutral' }
  ];

  const handlePageSelect = (pageId) => {
    setSelectedPages(prev =>
      prev?.includes(pageId) ? prev?.filter(id => id !== pageId) : [...prev, pageId]
    );
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleBulkPublish = async () => {
    for (const id of selectedPages) {
      await pagesService?.update(id, { status: 'published' });
      await auditService?.log('PUBLISH', 'page', id, null, { status: 'published' });
    }
    setSelectedPages([]);
    loadPages();
  };

  const handleBulkUnpublish = async () => {
    for (const id of selectedPages) {
      await pagesService?.update(id, { status: 'draft' });
      await auditService?.log('UNPUBLISH', 'page', id, null, { status: 'draft' });
    }
    setSelectedPages([]);
    loadPages();
  };

  const handleBulkArchive = async () => {
    for (const id of selectedPages) {
      await pagesService?.update(id, { status: 'archived' });
      await auditService?.log('ARCHIVE', 'page', id, null, { status: 'archived' });
    }
    setSelectedPages([]);
    loadPages();
  };

  const handleBulkDelete = async () => {
    for (const id of selectedPages) {
      await pagesService?.delete(id);
      await auditService?.log('DELETE', 'page', id, null, null);
    }
    setSelectedPages([]);
    loadPages();
  };

  const handleDeletePage = async (page) => {
    if (!page?.id) return;
    await pagesService?.delete(page?.id);
    await auditService?.log('DELETE', 'page', page?.id, { title: page?.title }, null);
    loadPages();
  };

  const handleEditPage = (page) => {
    console.log('Editing page:', page);
  };

  const handleDuplicatePage = async (page) => {
    if (!page?.id) return;
    const { data: original } = await pagesService?.getById(page?.id);
    if (original) {
      await pagesService?.create({
        title: `${original?.title} (Copy)`,
        slug: `${original?.slug}-copy-${Date.now()}`,
        content: original?.content,
        status: 'draft',
        pageType: original?.pageType
      });
      loadPages();
    }
  };

  const handleDragStart = (e, page) => { e?.dataTransfer?.setData('pageId', page?.id); };
  const handleDragOver = (e) => { e?.preventDefault(); };
  const handleDrop = (e, targetPage) => { e?.preventDefault(); };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar isCollapsed={sidebarCollapsed} onToggleCollapse={setSidebarCollapsed} />
      <Header isCollapsed={sidebarCollapsed} userName={user?.fullName || user?.name || 'User'} userRole={user?.role || 'Admin'} />
      <main className={`main-content with-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
        <div className="p-4 md:p-6 lg:p-8">
          <div className="mb-4 md:mb-6">
            <Breadcrumbs items={breadcrumbItems} />
          </div>

          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6 md:mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2">Page Manager</h1>
              <p className="text-sm md:text-base text-muted-foreground">
                Organize and manage your website structure with drag-and-drop simplicity
              </p>
            </div>
            <QuickActions
              onNewPage={() => console.log('New page')}
              onImport={() => console.log('Import')}
              onExport={() => console.log('Export')}
              onSettings={() => console.log('Settings')}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
            {stats?.map((stat, index) => (
              <PageStatsCard key={index} {...stat} />
            ))}
          </div>

          <div className="mb-6">
            <PageFilters
              filters={filters}
              onFilterChange={handleFilterChange}
              onSearch={setSearchQuery}
              searchQuery={searchQuery}
            />
          </div>

          <div className="bg-card border border-border rounded-lg p-4 md:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <h2 className="text-lg md:text-xl font-semibold text-foreground">Site Structure</h2>
              <ViewToggle currentView={currentView} onViewChange={setCurrentView} />
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Icon name="Loader2" size={24} className="text-primary animate-spin mr-2" />
                <span className="text-muted-foreground">Loading pages...</span>
              </div>
            ) : error ? (
              <div className="flex items-center gap-2 p-4 bg-error/10 border border-error/20 rounded-lg text-error">
                <Icon name="AlertCircle" size={16} />
                <span className="text-sm">{error}</span>
                <button onClick={loadPages} className="ml-auto text-xs underline">Retry</button>
              </div>
            ) : pagesData?.length === 0 ? (
              <div className="text-center py-12">
                <Icon name="FileText" size={40} className="text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No pages found. Create your first page!</p>
              </div>
            ) : (
              <div className="space-y-2">
                {pagesData?.map(page => (
                  <PageTreeNode
                    key={page?.id}
                    page={page}
                    selectedPages={selectedPages}
                    onSelect={handlePageSelect}
                    onToggle={() => {}}
                    onDragStart={handleDragStart}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    onEdit={handleEditPage}
                    onDuplicate={handleDuplicatePage}
                    onDelete={handleDeletePage}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
      <BulkActionsBar
        selectedCount={selectedPages?.length}
        onPublish={handleBulkPublish}
        onUnpublish={handleBulkUnpublish}
        onArchive={handleBulkArchive}
        onDelete={handleBulkDelete}
        onClearSelection={() => setSelectedPages([])}
      />
    </div>
  );
};

export default PageManager;